import { supabase } from '../supabase';
import {
  type TariffConfiguration,
  parseTariffConfiguration,
} from '../utils/presettlement-tariffs';
import {
  parseGrowerExceptions,
  type GrowerException,
} from '../utils/presettlement-grower-exceptions';
import {
  inheritedFromSeason,
  pickLatestSeasonRows,
} from '../utils/inheritedSeasonSettings';
import { Session } from '@supabase/supabase-js';

export interface ExporterSetting {
  id?: number;
  exporter_id: number;
  season: string;
  species_id: number;
  type: string;
  value: unknown; // JSONB can store any JSON value
  created_at?: string;
  updated_at?: string;
}

export type PreSettlementCalculationCriteria =
  | 'reference'
  | 'ship'
  | 'reception'
  | 'process'
  | 'packing'
  | 'etd'
  | 'eta';

export interface PreSettlementSettings {
  calculation_criteria: PreSettlementCalculationCriteria;
  product_definition: string[];
  cost_type: 'sameCost' | 'formatCost';
  commission_percentage: number;
  cost_per_format: { [format: string]: number };
  tariff_configuration?: TariffConfiguration | null;
  grower_exceptions?: GrowerException[];
  consider_extra_revenue?: boolean;
  // Temporada de la que se heredó la configuración (solo al cargar).
  inherited_from_season?: string | null;
}

// Lista guardada y la temporada de la que se heredó (null si es propia).
export interface InheritedList {
  values: string[];
  inheritedFrom: string | null;
}

/**
 * Filas de `types` de la temporada más reciente hasta `season`: una temporada
 * sin configuración hereda la de la anterior.
 */
async function loadLatestSeasonSettings(
  exporterId: number,
  season: string,
  speciesId: number,
  types: string[]
): Promise<{ rows: ExporterSetting[]; inheritedFrom: string | null }> {
  const { data, error } = await supabase
    .from('exporter_settings')
    .select('*')
    .eq('exporter_id', exporterId)
    .eq('species_id', speciesId)
    .in('type', types);

  if (error) throw error;

  const latest = pickLatestSeasonRows((data ?? []) as ExporterSetting[], season);
  return {
    rows: latest.rows,
    inheritedFrom: inheritedFromSeason(latest.season, season),
  };
}

// Pre-settlement setting types
export const PRESETTLEMENT_SETTING_TYPES = {
  CALCULATION_CRITERIA: 'presettlement_calculation_criteria',
  PRODUCT_DEFINITION: 'presettlement_product_definition',
  COST_TYPE: 'presettlement_cost_type',
  COMMISSION_PERCENTAGE: 'presettlement_commission_percentage',
  COST_PER_FORMAT: 'presettlement_cost_per_format',
  TARIFF_CONFIGURATION: 'presettlement_tariff_configuration',
  GROWER_EXCEPTIONS: 'presettlement_grower_exceptions',
  CONSIDER_EXTRA_REVENUE: 'presettlement_consider_extra_revenue',
} as const;

// Market data commission settings
export const MARKET_DATA_SETTING_TYPE = 'market_data_commissions';

// Favorite settings
export const FAVORITE_EXPORTERS_TYPE = 'favorite_exporters';
export const FAVORITE_BRANDS_TYPE = 'favorite_brands';

export interface MarketDataCommissions {
  receiver_commission: number; // % sobre ventas (default 8%)
  vat_tax: number; // % sobre ventas (default 9%)
  air_freight_cost: number; // USD/Kg (default 7.0)
  ocean_freight_cost: number; // USD/Kg (default 0.6)
  air_other_costs: number; // USD/Kg (default 0.5)
  ocean_other_costs: number; // USD/Kg (default 0.15)
  exporter_commission: number; // % sobre FOB (default 8%)
  packing_cost: number; // USD/Kg (default 2.0)
}

export const DEFAULT_MARKET_DATA_COMMISSIONS: MarketDataCommissions = {
  receiver_commission: 8,
  vat_tax: 9,
  air_freight_cost: 7.0,
  ocean_freight_cost: 0.6,
  air_other_costs: 0.5,
  ocean_other_costs: 0.15,
  exporter_commission: 8,
  packing_cost: 2.0,
};

/**
 * Save or update exporter pre-settlement settings for a specific season and species
 */
export async function savePreSettlementSettings(
  exporterId: number,
  season: string,
  speciesId: number,
  settings: PreSettlementSettings,
  session: Session | null
): Promise<boolean> {
  if (!session) {
    console.error('No session provided');
    return false;
  }

  try {
    // Prepare settings to save
    const settingsToSave: Omit<ExporterSetting, 'id' | 'created_at' | 'updated_at'>[] = [
      {
        exporter_id: exporterId,
        season,
        species_id: speciesId,
        type: PRESETTLEMENT_SETTING_TYPES.CALCULATION_CRITERIA,
        value: settings.calculation_criteria,
      },
      {
        exporter_id: exporterId,
        season,
        species_id: speciesId,
        type: PRESETTLEMENT_SETTING_TYPES.PRODUCT_DEFINITION,
        value: settings.product_definition,
      },
      {
        exporter_id: exporterId,
        season,
        species_id: speciesId,
        type: PRESETTLEMENT_SETTING_TYPES.COST_TYPE,
        value: settings.cost_type,
      },
      {
        exporter_id: exporterId,
        season,
        species_id: speciesId,
        type: PRESETTLEMENT_SETTING_TYPES.COMMISSION_PERCENTAGE,
        value: settings.commission_percentage,
      },
      {
        exporter_id: exporterId,
        season,
        species_id: speciesId,
        type: PRESETTLEMENT_SETTING_TYPES.COST_PER_FORMAT,
        value: settings.cost_per_format,
      },
    ];

    if (settings.consider_extra_revenue !== undefined) {
      settingsToSave.push({
        exporter_id: exporterId,
        season,
        species_id: speciesId,
        type: PRESETTLEMENT_SETTING_TYPES.CONSIDER_EXTRA_REVENUE,
        value: settings.consider_extra_revenue,
      });
    }

    if (settings.tariff_configuration) {
      const config = parseTariffConfiguration(settings.tariff_configuration);
      if (!config) return false;
      settingsToSave.push({
        exporter_id: exporterId,
        season,
        species_id: speciesId,
        type: PRESETTLEMENT_SETTING_TYPES.TARIFF_CONFIGURATION,
        value: config,
      });
    }

    if (settings.grower_exceptions !== undefined) {
      const exceptions = parseGrowerExceptions(settings.grower_exceptions);
      if (!exceptions) return false;
      settingsToSave.push({
        exporter_id: exporterId,
        season,
        species_id: speciesId,
        type: PRESETTLEMENT_SETTING_TYPES.GROWER_EXCEPTIONS,
        value: exceptions,
      });
    }

    // Use upsert to insert or update each setting
    for (const setting of settingsToSave) {
      const { error } = await supabase
        .from('exporter_settings')
        .upsert(setting, {
          onConflict: 'exporter_id,season,species_id,type',
        });

      if (error) {
        console.error('Error saving setting:', setting.type, error);
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('Unexpected error saving pre-settlement settings:', error);
    return false;
  }
}

/**
 * Load exporter pre-settlement settings for a specific season and species.
 * Without saved settings, inherits the most recent previous season.
 */
export async function loadPreSettlementSettings(
  exporterId: number,
  season: string,
  speciesId: number,
  session: Session | null
): Promise<PreSettlementSettings | null> {
  if (!session) {
    throw new Error('No session provided');
  }

  try {
    const { rows: data, inheritedFrom } = await loadLatestSeasonSettings(
      exporterId,
      season,
      speciesId,
      [
        PRESETTLEMENT_SETTING_TYPES.CALCULATION_CRITERIA,
        PRESETTLEMENT_SETTING_TYPES.PRODUCT_DEFINITION,
        PRESETTLEMENT_SETTING_TYPES.COST_TYPE,
        PRESETTLEMENT_SETTING_TYPES.COMMISSION_PERCENTAGE,
        PRESETTLEMENT_SETTING_TYPES.COST_PER_FORMAT,
        PRESETTLEMENT_SETTING_TYPES.TARIFF_CONFIGURATION,
        PRESETTLEMENT_SETTING_TYPES.GROWER_EXCEPTIONS,
        PRESETTLEMENT_SETTING_TYPES.CONSIDER_EXTRA_REVENUE,
      ]
    );

    if (data.length === 0) {
      // No settings found - this is normal for first time users
      return null;
    }

    // Convert the key-value pairs back to the structured format
    const settings: Partial<PreSettlementSettings> = {};
    
    data.forEach((setting: ExporterSetting) => {
      switch (setting.type) {
        case PRESETTLEMENT_SETTING_TYPES.CALCULATION_CRITERIA:
          settings.calculation_criteria = setting.value;
          break;
        case PRESETTLEMENT_SETTING_TYPES.PRODUCT_DEFINITION:
          settings.product_definition = setting.value;
          break;
        case PRESETTLEMENT_SETTING_TYPES.COST_TYPE:
          settings.cost_type = setting.value;
          break;
        case PRESETTLEMENT_SETTING_TYPES.COMMISSION_PERCENTAGE:
          settings.commission_percentage = setting.value;
          break;
        case PRESETTLEMENT_SETTING_TYPES.GROWER_EXCEPTIONS: {
          const exceptions = parseGrowerExceptions(setting.value);
          if (!exceptions) throw new Error('Invalid grower exceptions');
          settings.grower_exceptions = exceptions;
          break;
        }
        case PRESETTLEMENT_SETTING_TYPES.TARIFF_CONFIGURATION:
          settings.tariff_configuration = parseTariffConfiguration(setting.value);
          break;
        case PRESETTLEMENT_SETTING_TYPES.CONSIDER_EXTRA_REVENUE:
          settings.consider_extra_revenue = setting.value !== false;
          break;
        case PRESETTLEMENT_SETTING_TYPES.COST_PER_FORMAT:
          settings.cost_per_format = setting.value;
          break;
      }
    });

    // Only return settings if we have at least some data
    if (Object.keys(settings).length === 0) {
      return null;
    }

    settings.inherited_from_season = inheritedFrom;
    return settings as PreSettlementSettings;
  } catch (error) {
    console.error('Unexpected error loading pre-settlement settings:', error);
    throw error;
  }
}

/**
 * Delete exporter pre-settlement settings for a specific season and species
 */
export async function deletePreSettlementSettings(
  exporterId: number,
  season: string,
  speciesId: number,
  session: Session | null
): Promise<boolean> {
  if (!session) {
    console.error('No session provided');
    return false;
  }

  try {
    const { error } = await supabase
      .from('exporter_settings')
      .delete()
      .eq('exporter_id', exporterId)
      .eq('season', season)
      .eq('species_id', speciesId)
      .in('type', [
        PRESETTLEMENT_SETTING_TYPES.CALCULATION_CRITERIA,
        PRESETTLEMENT_SETTING_TYPES.PRODUCT_DEFINITION,
        PRESETTLEMENT_SETTING_TYPES.COST_TYPE,
        PRESETTLEMENT_SETTING_TYPES.COMMISSION_PERCENTAGE,
        PRESETTLEMENT_SETTING_TYPES.COST_PER_FORMAT,
        PRESETTLEMENT_SETTING_TYPES.TARIFF_CONFIGURATION,
        PRESETTLEMENT_SETTING_TYPES.GROWER_EXCEPTIONS,
        PRESETTLEMENT_SETTING_TYPES.CONSIDER_EXTRA_REVENUE,
      ]);

    if (error) {
      console.error('Error deleting pre-settlement settings:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Unexpected error deleting pre-settlement settings:', error);
    return false;
  }
}

/**
 * Get all saved settings for an exporter (for debugging or admin purposes)
 */
export async function getAllPreSettlementSettings(
  exporterId: number,
  session: Session | null
): Promise<ExporterSetting[]> {
  if (!session) {
    console.error('No session provided');
    return [];
  }

  try {
    const { data, error } = await supabase
      .from('exporter_settings')
      .select('*')
      .eq('exporter_id', exporterId)
      .in('type', [
        PRESETTLEMENT_SETTING_TYPES.CALCULATION_CRITERIA,
        PRESETTLEMENT_SETTING_TYPES.PRODUCT_DEFINITION,
        PRESETTLEMENT_SETTING_TYPES.COST_TYPE,
        PRESETTLEMENT_SETTING_TYPES.COMMISSION_PERCENTAGE,
        PRESETTLEMENT_SETTING_TYPES.COST_PER_FORMAT,
        PRESETTLEMENT_SETTING_TYPES.TARIFF_CONFIGURATION,
        PRESETTLEMENT_SETTING_TYPES.GROWER_EXCEPTIONS,
        PRESETTLEMENT_SETTING_TYPES.CONSIDER_EXTRA_REVENUE,
      ])
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Error loading all pre-settlement settings:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Unexpected error loading all pre-settlement settings:', error);
    return [];
  }
}

/**
 * Generic function to save any exporter setting
 */
export async function saveExporterSetting(
  exporterId: number,
  season: string,
  speciesId: number,
  type: string,
  value: unknown,
  session: Session | null
): Promise<boolean> {
  if (!session) {
    console.error('No session provided');
    return false;
  }

  try {
    const { error } = await supabase
      .from('exporter_settings')
      .upsert({
        exporter_id: exporterId,
        season,
        species_id: speciesId,
        type,
        value,
      }, {
        onConflict: 'exporter_id,season,species_id,type',
      });

    if (error) {
      console.error('Error saving exporter setting:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Unexpected error saving exporter setting:', error);
    return false;
  }
}

/**
 * Generic function to load exporter settings by type
 */
export async function loadExporterSettings(
  exporterId: number,
  season: string,
  speciesId: number,
  types: string[],
  session: Session | null
): Promise<ExporterSetting[]> {
  if (!session) {
    console.error('No session provided');
    return [];
  }

  try {
    const { data, error } = await supabase
      .from('exporter_settings')
      .select('*')
      .eq('exporter_id', exporterId)
      .eq('season', season)
      .eq('species_id', speciesId)
      .in('type', types);

    if (error) {
      console.error('Error loading exporter settings:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Unexpected error loading exporter settings:', error);
    return [];
  }
}

/**
 * Save market data commissions for an exporter
 * Note: This is season-agnostic (uses 'all' as season) since market data settings apply globally
 */
export async function saveMarketDataCommissions(
  exporterId: number,
  speciesId: number,
  commissions: MarketDataCommissions,
  session: Session | null
): Promise<boolean> {
  if (!session) {
    console.error('No session provided');
    return false;
  }

  try {
    const { error } = await supabase
      .from('exporter_settings')
      .upsert({
        exporter_id: exporterId,
        season: 'all', // Market data settings are not season-specific
        species_id: speciesId,
        type: MARKET_DATA_SETTING_TYPE,
        value: commissions,
      }, {
        onConflict: 'exporter_id,season,species_id,type',
      });

    if (error) {
      console.error('Error saving market data commissions:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Unexpected error saving market data commissions:', error);
    return false;
  }
}

/**
 * Load market data commissions for an exporter
 * Returns null if no settings exist (first time user)
 */
export async function loadMarketDataCommissions(
  exporterId: number,
  speciesId: number,
  session: Session | null
): Promise<MarketDataCommissions | null> {
  if (!session) {
    console.error('No session provided');
    return null;
  }

  try {
    const { data, error } = await supabase
      .from('exporter_settings')
      .select('*')
      .eq('exporter_id', exporterId)
      .eq('season', 'all')
      .eq('species_id', speciesId)
      .eq('type', MARKET_DATA_SETTING_TYPE)
      .maybeSingle();

    if (error) {
      console.error('Error loading market data commissions:', error);
      return null;
    }

    // maybeSingle() returns null if no row found, which is expected for first-time users
    return data?.value as MarketDataCommissions || null;
  } catch (error) {
    console.error('Unexpected error loading market data commissions:', error);
    return null;
  }
}

/**
 * Save favorite exporters for an exporter (by species and season)
 */
export async function saveFavoriteExporters(
  exporterId: number,
  season: string,
  speciesId: number,
  favoriteExporters: string[],
  session: Session | null
): Promise<boolean> {
  if (!session) {
    console.error('No session provided');
    return false;
  }

  return saveExporterSetting(
    exporterId,
    season,
    speciesId,
    FAVORITE_EXPORTERS_TYPE,
    favoriteExporters,
    session
  );
}

/**
 * Load favorite exporters for an exporter (by species and season). Without
 * a saved list, inherits the most recent previous season.
 */
export async function loadFavoriteExporters(
  exporterId: number,
  season: string,
  speciesId: number,
  session: Session | null
): Promise<InheritedList> {
  if (!session) {
    console.error('No session provided');
    return { values: [], inheritedFrom: null };
  }

  try {
    const { rows, inheritedFrom } = await loadLatestSeasonSettings(
      exporterId,
      season,
      speciesId,
      [FAVORITE_EXPORTERS_TYPE]
    );
    return {
      values: (rows[0]?.value as string[]) || [],
      inheritedFrom,
    };
  } catch (error) {
    console.error('Unexpected error loading favorite exporters:', error);
    return { values: [], inheritedFrom: null };
  }
}

/**
 * Save favorite brands for an exporter (by species and season)
 */
export async function saveFavoriteBrands(
  exporterId: number,
  season: string,
  speciesId: number,
  favoriteBrands: string[],
  session: Session | null
): Promise<boolean> {
  if (!session) {
    console.error('No session provided');
    return false;
  }

  return saveExporterSetting(
    exporterId,
    season,
    speciesId,
    FAVORITE_BRANDS_TYPE,
    favoriteBrands,
    session
  );
}

/**
 * Load favorite brands for an exporter (by species and season). Without a
 * saved list, inherits the most recent previous season.
 */
export async function loadFavoriteBrands(
  exporterId: number,
  season: string,
  speciesId: number,
  session: Session | null
): Promise<InheritedList> {
  if (!session) {
    console.error('No session provided');
    return { values: [], inheritedFrom: null };
  }

  try {
    const { rows, inheritedFrom } = await loadLatestSeasonSettings(
      exporterId,
      season,
      speciesId,
      [FAVORITE_BRANDS_TYPE]
    );
    return {
      values: (rows[0]?.value as string[]) || [],
      inheritedFrom,
    };
  } catch (error) {
    console.error('Unexpected error loading favorite brands:', error);
    return { values: [], inheritedFrom: null };
  }
}

